import { observable, action, computed } from 'mobx';
import { DeliveryOptions } from '../../../constants/Constants';
import SHIPPING_METHODS from '../../../constants/ShippingMethods';
import { COUNTRY_ID_AUSTRALIA } from '../../../utils/CountryUtils';
import { IPriceWithTaxConfig, getPriceWithTax } from '../../../utils/ShippingMethodUtils';

export interface IShippingMethod {
  discount?: number;
  skuId: number;
  sortOrder: number;
  name: string;
  price: number;
  selected?: boolean;
  id: SHIPPING_METHODS;
  totalPrice: number;
  countryIds?: number[];
  leadTimeValue?: number;
  nextAvailableSat?: string;
  estimatedDeliveryStartDate?: string;
  estimatedDeliveryEndDate?: string;
  availableWindow?: {
    startHour: number,
    stopHour: number,
    timeZone: string,
  }
}

const homeDeliveryShippingmethodIds = [
  SHIPPING_METHODS.STANDARD,
  SHIPPING_METHODS.STANDARD2,
  SHIPPING_METHODS.SECOND_DAY,
  SHIPPING_METHODS.SECOND_DAY_PM,
  SHIPPING_METHODS.OVERNIGHT,
  SHIPPING_METHODS.OVERNIGHT2,
  SHIPPING_METHODS.SATURDAY,
  SHIPPING_METHODS.NAMED_DAY,
];

export const mapDeliveryToMethods: Record<DeliveryOptions, SHIPPING_METHODS[]> = {
  [DeliveryOptions.HOME_DELIVERY]: homeDeliveryShippingmethodIds,
  [DeliveryOptions.SAME_DAY_DELIVERY]: [SHIPPING_METHODS.COURIER],
  [DeliveryOptions.CLICK_AND_COLLECT]: [SHIPPING_METHODS.BOPIS],
  [DeliveryOptions.PACKSTATION]: homeDeliveryShippingmethodIds,
  [DeliveryOptions.CONVENIENCE_STORE_PICK_UP]: [SHIPPING_METHODS.STANDARD2],
  [DeliveryOptions.UPS_STORE_PICK_UP]: [SHIPPING_METHODS.LOCAL_COLLECTION, SHIPPING_METHODS.STORE_COLLECTION],
  [DeliveryOptions.AU_POST_COLLECTIONS]: [SHIPPING_METHODS.STANDARD2, SHIPPING_METHODS.POST_EXPRESS],
  [DeliveryOptions.ELECTRONIC_DELIVERY]: [SHIPPING_METHODS.ELECTRONIC_SHIPPING],
};

export const DELIVERY_SHIP_METHOD_PAIRS_BY_COUNTRY: Record<number, Partial<Record<SHIPPING_METHODS, SHIPPING_METHODS>>> = {
  [COUNTRY_ID_AUSTRALIA]: {
    [SHIPPING_METHODS.STANDARD]: SHIPPING_METHODS.STANDARD2,
    [SHIPPING_METHODS.STANDARD2]: SHIPPING_METHODS.STANDARD,
    [SHIPPING_METHODS.SECOND_DAY]: SHIPPING_METHODS.POST_EXPRESS,
    [SHIPPING_METHODS.POST_EXPRESS]: SHIPPING_METHODS.SECOND_DAY,
  },
};

export class ShippingMethodsCollection {
  @observable items: IShippingMethod[];

  constructor(items: IShippingMethod[] = []) {
    this.items = items;
  }

  @action
  setItems = (items: IShippingMethod[]): void => {
    this.items = items;
  };

  @action
  hydrate(updateItems: IShippingMethod[]): void {
    this.items = this.items.reduce((acc, item) => {
      const updatedItem = updateItems.find(updateItem => updateItem.id === item.id) || { ...item };

      return [updatedItem, ...acc];
    }, []);
  }

  @computed
  get skuListIds(): number[] {
    return this.items.map(item => item.skuId);
  }

  getMethodsByDelivery(deliveryOption: DeliveryOptions): IShippingMethod[] {
    return this.items.filter(item => mapDeliveryToMethods[deliveryOption].includes(item.id));
  }

  getMinimumPriceByDelivery(deliveryOption: DeliveryOptions, config?: IPriceWithTaxConfig): number {
    const items = this
      .getMethodsByDelivery(deliveryOption)
      .filter(item => item.totalPrice !== undefined);

    const price = items.length > 0 ? items.reduce((prev, curr) => {
      const isPrevLower = prev && prev.totalPrice < curr.totalPrice;

      return isPrevLower ? prev : curr;
    }).totalPrice : 0;

    return config?.displayShippingFeeWithTax ? getPriceWithTax(price, config) : price;
  }

  getItemById(id: SHIPPING_METHODS | string): IShippingMethod {
    return this.items?.find(method => method.id === id);
  }

  hasShippingMethod(id: SHIPPING_METHODS | string): boolean {
    return !!this.getItemById(id);
  }
}
